import React from 'react';
import { CheckCircle, Clock, RefreshCw } from 'lucide-react';
import Button from '../../../../components/Button';

interface DeliverablePlanCardProps {
  categories: Array<Record<string, any>>;
  status: string;
  planApprovedAt?: string | null;
  onApprove: () => void;
  loading: boolean;
  onShuffle?: (index: number) => void;
  shufflingIndex?: number | null;
}

const DeliverablePlanCard: React.FC<DeliverablePlanCardProps> = ({ categories, status, planApprovedAt, onApprove, loading, onShuffle, shufflingIndex }) => {
  if (categories.length === 0) return null;

  const isApproved = !!planApprovedAt;
  const canApprove = ['planned', 'brief_approved'].includes(status) && !isApproved;

  return (
    <div className="rounded-xl border border-primary-100 bg-white p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-[15px] font-bold text-text-dark">Deliverable Plan</h3>
        <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[10px] font-semibold ${
          isApproved ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
        }`}>
          {isApproved ? <><CheckCircle className="h-3 w-3" /> Approved</> : <><Clock className="h-3 w-3" /> Needs Approval</>}
        </span>
      </div>

      <div className="space-y-2">
        {categories.map((item, i) => (
          <div key={`${item.category}-${i}`} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3.5 py-2.5">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-primary-200 text-[11px] font-bold text-brand-purple">
              {i + 1}
            </span>
            <span className="flex-1 text-xs font-semibold text-text-dark">{String(item.category || item.label)}</span>
            <span className="text-[10px] text-body-grey truncate max-w-[140px]">{String(item.templateName || item.rationale || '')}</span>
            <span className={`rounded-md px-2 py-0.5 text-[9px] font-semibold ${
              item.generationPath === 'scenescaper'
                ? 'bg-primary-100 text-brand-purple'
                : 'bg-green-50 text-green-600'
            }`}>
              {item.generationPath === 'scenescaper' ? 'SceneScaper' : 'Standard'}
            </span>
            {canApprove && onShuffle && item.generationPath !== 'scenescaper' && (
              <button
                type="button"
                onClick={() => onShuffle(i)}
                disabled={shufflingIndex != null}
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-body-grey hover:bg-primary-100 hover:text-brand-purple transition-colors disabled:opacity-40"
                title="Swap template"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${shufflingIndex === i ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canApprove && (
        <div className="mt-4">
          <Button variant="primary" size="small" onClick={onApprove} disabled={loading}>
            <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
            Approve Plan
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeliverablePlanCard;
